import { supabase } from './supabase';

const LS_KEY = 'travel_prefs';

// ── Save failure reporting ───────────────────────────────────────────────────
// Saves are fire-and-forget from ~20 call sites, so a failure used to reach
// nothing but console.error: a traveller could type their passport details and
// have the write fail with no sign of it. Failures are reported here instead,
// and the app surfaces one banner, rather than every caller handling its own.

const listeners = new Set();
let failing = false;

export function onSaveStatus(cb) {
  listeners.add(cb);
  cb(failing);
  return () => listeners.delete(cb);
}

function setFailing(v) {
  if (failing === v) return;
  failing = v;
  listeners.forEach(cb => { try { cb(failing); } catch (_) {} });
}

export function isSaveFailing() {
  return failing;
}

// One retry covers the common transient case (a dropped connection, an expired
// token that refreshes underneath) without turning a real outage into a loop.
async function writeProfile(payload) {
  for (let attempt = 0; attempt < 2; attempt++) {
    if (attempt > 0) await new Promise(r => setTimeout(r, 700));
    const { error } = await supabase
      .from('profiles')
      .upsert(payload, { onConflict: 'user_id' });
    if (!error) { setFailing(false); return true; }
    if (attempt === 1) {
      console.error('[userPrefs] save failed', error);
      setFailing(true);
    }
  }
  return false;
}

// ── Destinations / pax prefs ─────────────────────────────────────────────────

export async function loadPrefs(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error?.code === 'PGRST116') return null; // no profile row → new account → onboarding
  if (error) return { _error: true }; // auth failure or network — don't treat as new account
  if (!data) return null; // genuinely no profile row

  // Charger travel_prefs depuis Supabase et mettre en cache local
  if (data.travel_prefs) {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(data.travel_prefs));
    } catch (_) {}
  }

  return {
    pax: data.pax || 2,
    selectedIds: JSON.parse(data.selected_destinations || '[]'),
    travelPrefs: data.travel_prefs || null,
    lastRanking: data.last_ranking || null,
  };
}

export function savePrefs(userId, pax, selectedIds) {
  if (!userId) return Promise.resolve(false);
  return writeProfile({
    user_id: userId,
    pax,
    selected_destinations: JSON.stringify(selectedIds),
  });
}

// ── Travel preferences ───────────────────────────────────────────────────────

export function loadTravelPrefs() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || 'null'); }
  catch { return null; }
}

export function saveTravelPrefs(prefs, userId) {
  // Cache local immédiat — survit à un échec serveur et à un rechargement.
  try { localStorage.setItem(LS_KEY, JSON.stringify(prefs)); } catch (_) {}

  if (!userId) return Promise.resolve(false);
  return writeProfile({ user_id: userId, travel_prefs: prefs });
}
